import { Box, Stack, Typography, Button } from '@mui/material'
import DOMPurify from 'dompurify'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { COLORS, LinkUnstyled, optimizelyCss, unit } from '@hcr/ui'
import { isNotNull, isNotUndefined } from '@hcr/utils'

import { useHoliday } from '../../../contexts'
import { useTimeshareIntroductionInfoQuery, useIdToken, useAccommodationDetailsQuery } from '../../../hooks'
import { Path } from '../../../models'
import { isFlyAccommodation, to } from '../../../utils'
import { LayoutNavigationBack, Metadata } from '../../common'

import { BookingTimeshareIntroductionFallback } from './BookingTimeshareIntroductionFallback'
import { BookingTimeshareIntroductionLoading } from './BookingTimeshareIntroductionLoading'

const BACK_NAVIGATION_PATH = to(Path.Booking)

export const BookingTimeshareIntroduction: FC = () => {
  const idToken = useIdToken()
  const { t } = useTranslation()
  const holiday = useHoliday()

  const accommodation = useAccommodationDetailsQuery(
    { idToken: String(idToken), accommodationId: Number(holiday.data.accommodationId) },
    { enabled: isNotNull(idToken) && isNotNull(holiday.data.accommodationId) }
  )

  const hasTimeshareIntroductionInfo = isNotUndefined(accommodation.data) && isFlyAccommodation(accommodation.data)

  const timeshareIntroductionInfo = useTimeshareIntroductionInfoQuery({
    enabled: isNotNull(idToken) && hasTimeshareIntroductionInfo,
  })

  if (timeshareIntroductionInfo.isError) {
    return <BookingTimeshareIntroductionFallback />
  }

  if (!hasTimeshareIntroductionInfo) {
    return <Navigate to={Path.Booking} replace />
  }

  if (holiday.isSuccess && accommodation.isSuccess && timeshareIntroductionInfo.isSuccess) {
    return (
      <LayoutNavigationBack to={BACK_NAVIGATION_PATH} arrowVariant='text'>
        <Metadata title={t('page-title.booking-timeshare-introduction')} />
        <Box bgcolor={COLORS.white} minHeight='100vh'>
          <Stack gap={unit(4)} paddingTop={unit(24)} paddingX={unit(7)} paddingBottom={unit(4.5)}>
            <Typography component='h1' variant='headlineL'>
              {timeshareIntroductionInfo.data.title}
            </Typography>
            <Typography component='h2' variant='bodyM'>
              {timeshareIntroductionInfo.data.ingress}
            </Typography>

            <Typography paddingY={unit(2)} variant='titleS'>
              {timeshareIntroductionInfo.data.subHeading}
            </Typography>

            <Typography
              variant='bodyM'
              css={[optimizelyCss]}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(timeshareIntroductionInfo.data.content) }}
            />

            <Button
              component={LinkUnstyled}
              to={timeshareIntroductionInfo.data.formLink.url}
              sx={{ marginTop: unit(2) }}
              target='_blank'
              rel='external'
              variant='contained'
              color='primary'
            >
              {timeshareIntroductionInfo.data.formLink.label}
            </Button>
          </Stack>
        </Box>
      </LayoutNavigationBack>
    )
  }

  return <BookingTimeshareIntroductionLoading />
}
